import {action, makeAutoObservable, observable, runInAction} from 'mobx';
import {inject, injectable} from 'inversify';
import type {IJSONProvider} from 'data/providers/json/json.provider';
import {Bindings} from 'data/constants/bindings';
import {RoundStatus} from 'data/enums';
import {every, first, isEmpty, last} from 'lodash';
import {IS_RESULT_STATE} from 'data/constants';

export interface IRound {
	id: number;
	number: number;
	status: RoundStatus;
	startDate: string;
	endDate: string;
}

export interface IRoundsStore {
	get getIsLoading(): boolean;
	get list(): IRound[];
	get seasonsRoundsList(): IRound[];
	get playoffRoundsList(): IRound[];
	get selectedRoundId(): number;
	get scheduleRounds(): IRound[];
	get currentRound(): IRound | undefined;
	get scoreRound(): IRound | null;
	get actualRound(): IRound | undefined;
	get selectedRound(): IRound | undefined;
	get lastCompleteRound(): IRound | undefined;
	get actualStandingsRoundId(): number;
	get isRoundsLockout(): boolean;
	get isOffSeason(): boolean;
	get lastCompleteRoundId(): number;

	fetchRounds(): Promise<void>;
	setSelectedRoundId(roundId: number): void;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _isLoading: boolean = false;
	@observable private _selectedRoundId: number = 0;
	@observable private _list: IRound[] = [];

	get list() {
		return this._list;
	}

	get seasonsRoundsList() {
		return this._list.filter((round) => round.number < 11);
	}

	get seasonRoundsListCompleted() {
		return this.seasonsRoundsList.filter((e) => e.status === RoundStatus.Complete);
	}

	get playoffRoundsList() {
		return this._list.filter((round) => round.number > 8 && round.number <= 18);
	}

	get selectedRoundId() {
		return this._selectedRoundId;
	}

	get scheduleRounds() {
		return this.playoffRoundsList.filter((e) => e.status === RoundStatus.Scheduled);
	}

	get completedRounds() {
		return this.playoffRoundsList.filter((e) => e.status === RoundStatus.Complete);
	}

	get playingRound() {
		return this.playoffRoundsList.find((e) => e.status === RoundStatus.Playing);
	}

	get actualStandingsRoundId() {
		if (!this.lastCompleteRound) {
			// return this._list[0]?.id ?? 1;
			return last(this.seasonRoundsListCompleted)?.id ?? 1;
		}

		return this.playingRound?.id || this.lastCompleteRound.id;
	}

	get lastCompleteRound() {
		return last(this.completedRounds);
	}

	get lastCompleteRoundId() {
		if (!this.lastCompleteRound) {
			// return this._list[0]?.id ?? 1;
			return last(this.seasonRoundsListCompleted)?.id ?? 1;
		}
		return this.lastCompleteRound.id;
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentRound() {
		return this.activeRound || first(this.scheduleRounds) || last(this.playoffRoundsList);
	}

	get scoreRound() {
		return this.activeRound || last(this.completedRounds) || null;
	}

	private get activeRound() {
		return this.list.find((e) => e.status === RoundStatus.Playing);
	}

	get actualRound() {
		return (
			this.playoffRoundsList.find(({status}) =>
				[RoundStatus.Playing, RoundStatus.Scheduled].includes(status)
			) || last(this.playoffRoundsList)
		);
	}

	get selectedRound() {
		return this.playoffRoundsList.find((round) => round.id === this._selectedRoundId);
	}

	get isRoundsLockout() {
		return IS_RESULT_STATE || isEmpty(this.scheduleRounds);
	}

	get isOffSeason() {
		return IS_RESULT_STATE || every(this.playoffRoundsList.map((it) => it.status === RoundStatus.Complete));
	}

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@action setSelectedRoundId(roundId: number) {
		this._selectedRoundId = roundId;
	}

	@action
	async fetchRounds() {
		const {data} = await this._jsonProvider.rounds();

		runInAction(() => {
			this._list = data.items;
		});
	}
}
